"use client";
import { useEffect, useState } from "react";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "./components/lib/CustomButton";
import OfflinePage from "./components/Errors/offline/offline";
import CookiesBlockedPage from "./components/Errors/CookiesBlockedPage";
import { logClientSideError } from "@/utils/logging/clientErrorLogger";

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
  /** React component stack from error boundary's onError callback */
  componentStack?: string;
  /** When true, skip logging (ErrorBoundary already logged) */
  skipLogging?: boolean;
}

const ErrorPage: React.FC<ErrorPageProps> = ({
  error,
  reset,
  componentStack,
  skipLogging = false,
}) => {
  const [errorInfo, setErrorInfo] = useState<string>("");
  const [logStatus, setLogStatus] = useState<string>("");

  useEffect(() => {
    const isServerSide = !!error.digest;

    const info = isServerSide
      ? `Server Error: ${error.message}\nDigest: ${error.digest}`
      : `Client Error: ${error.message}\nStack: ${error.stack}`;

    setErrorInfo(info);

    // Only log if not already logged by ErrorBoundary
    if (!skipLogging) {
      logClientSideError(error, componentStack)
        .catch(() => {
          setLogStatus("Failed to send error log to Graylog");
        });
    }
  }, [error]);

  if (["ChunkLoadError"].includes(error.name)) return <OfflinePage />;
  if (["SecurityError"].includes(error.name)) return <CookiesBlockedPage />;

  return (
    <div className={"errorContainer"}>
      <div className={"errorContent"}>
        <h2>Oops! Something Went Wrong</h2>
        <p className={"errorMessage"}>
          We apologize for the inconvenience. Our team has been notified and is
          working on a fix.
        </p>
        <CustomButton
          type={BtnTypes.primary}
          size={BtnSizes.small}
          radius={BtnRadius.none}
          onClick={reset}
        >
          Try Again
        </CustomButton>
        <div className={"errorDetails"}>
          <h3>Error Details:</h3>
          <pre>{errorInfo}</pre>
          {logStatus && <p className={"logError"}>{logStatus}</p>}
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
